import { BillingV2Subscription } from "@screencloud/studio-billing-sdk/client";

export type BillingPeriodUnit = BillingV2Subscription["billingPeriodUnit"];

/**
 * Formats an amount in minor units (cents) as currency, e.g. `$240` or `£19.99`.
 *
 * @remarks
 * Whole amounts drop the decimals, matching how the billing pages have always shown prices.
 */
export const formatMoney = (
  currency: string,
  amountInCents: number,
  locale = "en-US",
): string => {
  const amount = amountInCents / 100;
  try {
    return amount.toLocaleString(locale, {
      style: "currency",
      currency,
      currencyDisplay: "narrowSymbol",
      minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
    });
  } catch {
    return amount.toLocaleString(locale);
  }
};

/**
 * Formats an invoice total in minor units with two decimals, e.g. `$240.00`.
 */
export const formatInvoiceAmount = (
  amountInCents: number,
  currency: string,
  locale = "en-US",
): string => {
  const amount = amountInCents / 100;
  try {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency,
    }).format(amount);
  } catch {
    return new Intl.NumberFormat(locale, {
      style: "decimal",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  }
};

/**
 * Removes a trailing currency code from a plan or add-on name: `Core (USD)` and `Core USD` both become `Core`.
 */
export const stripCurrencyCode = (name: string): string => {
  if (/\([A-Z]+\)/.test(name)) {
    return name.replace(/\([A-Z]+\)/g, "").trim();
  }
  return name.replace(/\s[A-Za-z]{3}$/g, "").trim();
};

/**
 * Formats a date as `November 9, 2026`.
 */
export const formatDate = (date: Date): string => {
  const month = date.toLocaleString("default", { month: "long" });
  return `${month} ${date.getDate()}, ${date.getFullYear()}`;
};

/**
 * The label for how often a subscription is billed, e.g. `Annually` or `Every 3 years`.
 */
export const termLabel = (period: number, unit: BillingPeriodUnit): string => {
  switch (unit) {
    case "year":
      return period === 1 ? "Annually" : `Every ${period} years`;
    case "week":
      return period === 1 ? "Weekly" : `Every ${period} weeks`;
    case "day":
      return period === 1 ? "Daily" : `Every ${period} days`;
    case "month":
      if (period === 1) {
        return "Monthly";
      }
      if (period === 3) {
        return "Quarterly";
      }
      if (period === 6) {
        return "Semi-annually";
      }
      return `Every ${period} months`;
    default:
      return "";
  }
};

/**
 * The unit a price is quoted per, e.g. `year` in `$240/screen/year`.
 */
export const periodLabel = (
  period: number,
  unit: BillingPeriodUnit,
): string => {
  switch (unit) {
    case "year":
      return period === 1 ? "year" : `${period} years`;
    case "week":
      return period === 1 ? "week" : `${period} weeks`;
    case "day":
      return period === 1 ? "day" : `${period} days`;
    case "month":
      if (period === 1) {
        return "month";
      }
      if (period === 3) {
        return "quarter";
      }
      if (period === 6) {
        return "semi-annual";
      }
      return `${period} months`;
    default:
      return "";
  }
};

/**
 * Turns `in_trial`, `non-renewing` or `inTrial` into `In Trial`.
 */
export const normalCase = (value: string): string =>
  value
    .replace(/[-_]/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\b\w/g, (match) => match.toUpperCase());

/**
 * `1 license`, `2 licenses`, `3 boxes`.
 */
export const pluralise = (word: string, count: number): string => {
  let plural = `${word}s`;
  if (/(s|sh|ch|x|z)$/i.test(word)) {
    plural = word.replace(/(s|sh|ch|x|z)$/i, "$1es");
  } else if (/[^aeiou]y$/i.test(word)) {
    plural = word.replace(/y$/i, "ies");
  }
  return `${count} ${count === 1 ? word : plural}`;
};
